import { TextTransform } from '@/composables/useTranslation'
import { AnyObject, NonEmptyValue, NumericObject } from '@/models/GenericInterfaces'
import { Country, CountryFlagSymbols, Culture, Option, SearchMode, Sorting } from '@/models/Global'
import { ProductType } from '@/models/Product'
import { ValueCallback } from '@/models/Property'
import { SimplifiedCategory } from '@/models/Taxonomy'
import { deleteField } from 'firebase/firestore'
import { Slot, capitalize } from 'vue'

export const sanitizeObjectProperties = (object: { [key: string]: any }, deleteValue?: string) => {
  if (object && typeof object === 'object') {
    // check both since null is of type object

    Object.entries(object).forEach(([key, value]) => {
      if (deleteValue !== undefined && value === deleteValue) {
        object[key] = deleteField()
      } else if (value === undefined || value === null) {
        delete object[key]
      } else if (value && typeof value === 'object') {
        sanitizeObjectProperties(value, deleteValue)
        if (!Object.keys(value).length) {
          delete object[key]
        }
      }
    })
  }
}

export const clone = <T = undefined>(obj: T | undefined) => (obj ? JSON.parse(JSON.stringify(obj)) : obj) as T
// export const clone = (obj: AnyObject | undefined) => (obj ? JSON.parse(JSON.stringify(obj)) : obj)

/**
 * Recursive method used by mergeObjects that merges two objects
 * Some problems merging arrays
 * @param obj does not modify this object
 * @param mergeIntoObject modifies this object
 * @param mergeArraysOnKeys if false, merges array by concatenating
 */
const merge = (obj: AnyObject, mergeIntoObject: AnyObject, mergeArraysOnKeys: boolean) => {
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (!mergeArraysOnKeys && Array.isArray(obj) && Array.isArray(mergeIntoObject)) {
      mergeIntoObject.push(value)
      mergeIntoObject = mergeIntoObject.sort((a, b) => a.field?.localeCompare(b.field)) //.filter((v) => Boolean(v))
    } else if (mergeIntoObject[key] === undefined && value !== undefined) {
      mergeIntoObject[key] = clone(value)
    } else if (typeof value === 'number') {
      mergeIntoObject[key] = (mergeIntoObject[key] || 0) + value
    } else if (typeof value === 'object') {
      merge(value, mergeIntoObject[key], mergeArraysOnKeys)
    }
  })
}

/**
 * Merges objects by creating a new object. does not modify the originals
 * Sums values if they are numbers, otherwise the value from the first object will be used
 * @param mergeArraysOnKeys if false, merges array by concatenating
 * @param  {...any} objs
 * @returns
 */
export const mergeObjects = <T extends AnyObject>(mergeArraysOnKeys = false, ...objs: AnyObject[]) => {
  const newObject: AnyObject = {}
  objs.forEach((obj) => merge(obj, newObject, mergeArraysOnKeys)) // merges one object at a time to new total object
  return newObject as T
}

/**
 * Converts to lower case string and trims
 * Use to compare strings
 * @param name
 * @returns
 */
export const toComparable = (name: unknown): string => toSafeString(name).trim().toLowerCase()

export const areComparable = (value1: unknown, value2: unknown) => toComparable(value1) === toComparable(value2)

export const toTitle = (str: string | number) => capitalize(camelCaseToTerm(str))

/**
 * @returns true if value is falsy and not 0. if value is an array it return true if the array is empty
 */
export const valueIsEmpty = (value: unknown) => (Array.isArray(value) ? !value.length : value !== 0 && !value) // && value !== null

/**
 * Checks whether value, including 0 as a truthy value
 * Lightweight inverse of valueIsEmpty (doesn't check array)
 */
export const hasValue = <T>(value: T): value is NonEmptyValue<T> => !!value || value === 0 || value === 0n

/**
 * Turns truthy values (except empty arrays) and 0 to string
 */
export const toSafeString = (value: unknown) => (valueIsEmpty(value) ? '' : String(value))

export const clamp = (value: number, min: number, max: number): number => Math.max(Math.min(value, max), min)

/**
 * Adds wrapper to mapping
 */
export const toMapping = (mapping: string) => `{{${mapping}}}`

/**
 * Removes potential wrapper from mapping
 */
export const stripMapping = (mapping: string | number | undefined) => mapping?.toString()?.replace(/{{(.*)}}/, '$1') || ''

// export const toOptions = (arr: string[] | readonly string[] | undefined): Option[] => arr?.map((item) => ({ value: item, displayValue: item })) || []
export const toOptions = <T extends string>(arr: T[] | readonly T[] | undefined): Option<T>[] => arr?.map((item) => ({ value: item, displayValue: item })) || []

/**
 * Generic function
 */
export const setEnsuredInnerValue = (obj: AnyObject, keyPath: Array<string | number>, value: any, overwriteValue = true) => {
  if (keyPath.length) {
    for (let i = 0; i < keyPath.length - 1; i++) {
      const key = keyPath[i]
      if (!obj[key]) {
        obj[key] = {}
      }
      obj = obj[key]
    }

    const lastKey = keyPath[keyPath.length - 1]
    if (!obj[lastKey] || overwriteValue) {
      obj[lastKey] = value
    }
  }
}

export const getInnerValue = <T = unknown>(obj: AnyObject | undefined, keyHierarchy: Array<string | number | ValueCallback>, level = 0): T | undefined => {
  try {
    if (obj) {
      const key = keyHierarchy[level]

      const value = typeof key === 'function' ? key(obj) : key ? obj[key] : obj
      if (level < keyHierarchy.length - 1 && value) {
        return getInnerValue(value, keyHierarchy, level + 1)
      } else {
        if (!keyHierarchy.length) {
          // console.log(key, value)
        }
      }
      // return typeof value === 'object' ? value.value || value.name || value.defaultName : value
      return value as T
    }
  } catch (err) {
    console.log('getInnerValue', err)
  }
}

/**
 * @returns true if value was found and deleted
 */
export const deleteInnerValue = (obj: AnyObject | undefined, keyHierarchy: Array<string>) => {
  let path = obj

  for (let i = 0; i < keyHierarchy.length; i++) {
    const key = keyHierarchy[i]

    if (i < keyHierarchy.length - 1) {
      path = path?.[key]
    } else if (path && key in path) {
      delete path[key]
      return true
    }
  }

  return false
}

export const camelCaseToTerm = (camelCaseName: string | number | undefined, capitalizeTerm = true): string => {
  const str = toSafeString(camelCaseName)

  const result = str.replace(/([a-z])([A-Z])/g, `$1 $2`).replace(/([A-Z](?=[a-z]))/g, (g1) => (g1 + '')?.toLowerCase())
  return capitalizeTerm ? capitalize(result) : result
}

export const toCamelCase = (str: string | number | undefined): string => {
  str = toSafeString(str)
  return str.slice(0, 1).toLowerCase() + str.slice(1).replace(/(\s[a-z])/gi, (g1) => g1.toUpperCase().trim())
}

/**
 * Pushes new or modifies existing item in array
 */
export const toggleItemInArray = (array: Array<string | number | boolean>, item: string | number | boolean) => {
  const foundIndex = array.findIndex((includedItem) => includedItem == item)
  if (foundIndex > -1) {
    array.splice(foundIndex, 1)
  } else {
    array.push(item)
  }
}

export const sortHierarchically = (categories: SimplifiedCategory[], intiallySortByName = true) => {
  if (intiallySortByName) {
    categories?.sort((a, b) => toComparable(a.name).localeCompare(toComparable(b.name)) || 0)
  }
  return recursiveSort(categories)
}

/**
 * Helper function for sortHierarchically
 */
// const recursiveSort = (objects?: SimplifiedCategory[], parentId?: string, indentation = ''): Array<SimplifiedCategory & { indentation?: string }> =>
// const recursiveSort = <T extends { parentId?: string; id: string }>(objects?: T[], parentId?: string, indentation = ''): Array<T & { indentation?: string }> =>
//   objects
//     ?.filter((object) => object.parentId === parentId)
//     ?.flatMap((object) => [{ ...object, indentation }, ...recursiveSort(objects, object.id, indentation + '\xA0\xA0\xA0')]) || []

const recursiveSort = <T extends { parentId?: string; id: string }>(objects: T[], parentId?: string) => {
  if (!objects?.length) return []

  const childrenMap = new Map<string | undefined, T[]>()
  for (const obj of objects) {
    const children = childrenMap.get(obj.parentId)
    if (children) {
      children.push(obj)
    } else {
      childrenMap.set(obj.parentId, [obj])
    }
  }

  const result: Array<T & { level?: number }> = []

  const traverse = (pid: string | undefined, level = 0) => {
    const children = childrenMap.get(pid)
    if (children) {
      for (const child of children) {
        result.push({ ...child, level })
        traverse(child.id, level + 1)
      }
    }
  }

  traverse(parentId)
  return result
}

export const getIndentation = (level = 0) => {
  let indentation = ''

  for (let i = 0; i < level; i++) {
    indentation += '\xA0\xA0\xA0'
  }

  return indentation
}

/**
 *
 * @param data the array to sort. mutates the array
 * @param sorting if not supplied, currentSorting settings is used
 * @param altSortingKeyPath uses the inner value of this keypath to sort by if sorting field is not found on the objects in arr
 */
// export const sortList = (data: any[], currentSorting: Sorting, newSorting?: Sorting, altSortingKeyPath?: string[]) => {
export const sortList = (data: any[] | undefined, sorting: Sorting, altSortingKeyPath?: string[]) =>
  data?.sort((a: any, b: any) => {
    const fieldA = String(a[sorting.field] || (altSortingKeyPath ? getInnerValue(a, altSortingKeyPath) : 0))
    const fieldB = String(b[sorting.field] || (altSortingKeyPath ? getInnerValue(b, altSortingKeyPath) : 0))

    const secondarySorting = String(a[sorting.secondaryField || ''])
      .toLowerCase()
      .localeCompare(String(b[sorting.secondaryField || '']).toLowerCase())
    if (sorting.order === 'desc') {
      const descNumeric = Number(fieldB) - Number(fieldA)
      return (isNaN(descNumeric) ? fieldB?.localeCompare(fieldA) : descNumeric) || secondarySorting
    } else {
      const ascNumeric = Number(fieldA) - Number(fieldB)
      return (isNaN(ascNumeric) ? fieldA?.localeCompare(fieldB) : ascNumeric) || secondarySorting
    }
  })

/**
 * Function that sort objects numerically if applicable and the string parts are equal, else lexiographically
 * @param objects
 * @param sortField the field to sort by
 * @param sortOrder the default sort order
 * @param sortOrderNumeric sort order for numeric fields (usually categories including year),
 * @param numericBaseLine in order to sort e.g. the numeric parts as year when the format differs, e.g. YYYY and YY/YY (then set numericBaseLine to 2000)
 */
export const sortLexioNumerically = <T>(
  objects: T[] | undefined,
  sortField: keyof T,
  sortOrder = 'desc' as 'asc' | 'desc',
  sortOrderNumeric?: 'asc' | 'desc',
  numericBaseLine?: number,
  secondarySortField?: keyof T,
) => {
  const isNumeric = (str: string) => /\d/.test(str)

  const maybeAddNumericBaseline = (value: unknown) => {
    if (numericBaseLine && value !== undefined) {
      return String(value).replace(/\b\d{2}\b/g, (match) => `${numericBaseLine + parseInt(match)}`)
    }
    return String(value === undefined ? '' : value)
  }

  return objects?.sort((a, b) => {
    const fieldA = maybeAddNumericBaseline(a[sortField] || (secondarySortField ? a[secondarySortField] : undefined))
    const fieldB = maybeAddNumericBaseline(b[sortField] || (secondarySortField ? b[secondarySortField] : undefined))

    if (fieldA === 'translatenone') {
      // put uncategorized last
      return 1
    }

    const sorting = isNumeric(fieldA) ? sortOrderNumeric : sortOrder

    if (sorting === 'asc') {
      return fieldA.localeCompare(fieldB)
    } else {
      return fieldB.localeCompare(fieldA)
    }
  })
}

/**
 *
 * @param data
 * @param currentSorting mutates object
 * @param newSorting
 * @param altSortingKeyPath
 * @param lexioNumericSort mainly for categories. separates the string and numeric parts and sorts numerically if the string parts are equal. adds "2000" to the numeric part if the number is below 2000 (to be able to sort e.g. "2022" and "22/23" correctly)
 * @returns
 */
export const sort = (
  data: any[] | undefined,
  currentSorting: Sorting | undefined,
  newSorting: Sorting,
  altSortingKeyPath?: string[],
  lexioNumericSort = false,
) => {
  if (currentSorting) {
    currentSorting.field = newSorting.field
    currentSorting.order = newSorting.order
  }
  if (lexioNumericSort) {
    return sortLexioNumerically(data, newSorting.field, newSorting.order, newSorting.order, 2000)
  } else {
    return sortList(data, newSorting, altSortingKeyPath)
  }
}

export const flattenObjectValues = (obj: AnyObject): unknown[] =>
  Object.values(obj || {})
    .map((o) => (typeof o === 'object' ? flattenObjectValues(o) : o))
    .flat()

const _removeEmptyObjects = (obj: any | undefined, maxDepth?: number, depth = 0, excludeKeys?: Set<string>) => {
  if (!maxDepth || depth < maxDepth) {
    Object.entries(obj || {}).forEach(([key, innerObj]) => {
      if (typeof innerObj === 'object' && !excludeKeys?.has(key)) {
        _removeEmptyObjects(innerObj, maxDepth, depth + 1, excludeKeys)

        if (Object.keys(innerObj || {}).length === 0 && innerObj !== null) {
          // keep null since those are occassionally used to symbolize deletion
          delete obj[key]
        }
      }
    })
  }
}

export const removeEmptyObjects = (obj: any | undefined, maxDepth?: number, excludeKeys?: Set<string>) => _removeEmptyObjects(obj, maxDepth, 0, excludeKeys)

export const startingLowerCaseForNonAcronyms = (str: string | undefined) => {
  if (str?.length && str.charAt(1).toLowerCase() === str.charAt(1) && !parseInt(str.charAt(1))) {
    return str.substring(0, 1).toLowerCase() + str.substring(1)
  }
  return str
}

export const roundToPrecision = (value: number, decimalPrecision: number): number => {
  const multiplicator = Math.pow(10, decimalPrecision)
  return Math.round(toNumber(value) * multiplicator) / multiplicator
}

export const multiplicate = (arr: number[]) => arr.reduce((product, value) => (product *= toNumber(value)), 1)
export const multiplicateObject = (obj: NumericObject): number => multiplicate(Object.values(obj))

export const sumArray = (arr: number[]) => arr.reduce((sum, value) => (sum += toNumber(value)), 0)

export const sumObject = (obj: { [key: string]: number }): number => sumArray(Object.values(obj))

export const sumNonEmptyObjects = (arr: unknown[]) => arr.reduce((sum: number, value) => (sum += value ? 1 : 0), 0)

export const toNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return value
  }

  if (value || value === '0') {
    const n = value?.toString().replace(',', '.').replace(/\xA0/g, '')
    return Number(n)
  }

  return NaN
}

export const isNumeric = (value: unknown) => /^[-+]?(?:[0-9]+([.,][0-9]*)?|[0-9]*[.,][0-9]+)$/.test(toSafeString(value))

/** Returns a string representation of the number with fixed decimals in supplied locale */
export const numberToFixedDecimals = (
  value: unknown,
  decimals = 2,
  locale?: Intl.LocalesArgument,
  currency?: { string: string; display: Intl.NumberFormatOptions['currencyDisplay'] },
) => {
  const numericValue = toNumber(value)
  const formatOptions: Intl.NumberFormatOptions = currency ? { style: 'currency', currency: currency.string, currencyDisplay: currency.display } : {}

  // if (!Number.isInteger(numericValue)) {
  formatOptions.minimumFractionDigits = decimals
  formatOptions.maximumFractionDigits = decimals
  // }

  return numericValue.toLocaleString(locale, formatOptions)
}

export const isScrolledToBottom = (el: HTMLElement | undefined, margin = 5) => {
  if (el) {
    return el.offsetHeight + el.scrollTop >= el.scrollHeight - margin
  }
  return false
}

export const objectsAreEqual = (
  obj1: AnyObject | string | number | null | undefined,
  obj2: AnyObject | string | number | null | undefined,
  excludeKeys?: Set<string>,
  strict = true,
) => {
  if (strict && typeof obj1 !== typeof obj2) {
    return false
  }

  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const obj1Keys = Object.keys(obj1 || {})
    const obj2Keys = Object.keys(obj2 || {})

    if (obj1Keys.length !== obj2Keys.length) {
      return false
    }

    for (const key of obj1Keys) {
      if (!excludeKeys?.has(key) && !objectsAreEqual(obj1?.[key], obj2?.[key], excludeKeys, strict)) {
        return false
      }
    }

    return true
  } else {
    if (strict) {
      return obj1 === obj2
    }
    return obj1 == obj2
  }
}

export const transformString = (str: string, transform?: TextTransform) => {
  switch (transform) {
    case 'capitalize':
      return capitalize(str)
    case 'uppercase':
      return str?.toUpperCase()
    case 'lowercase':
      return str?.toLowerCase()
  }
  return str
}

export const searchHaystack = (searchTerm: string | undefined = '', haystack: unknown[] | undefined, mode: SearchMode = 'split-words') => {
  const nonRegexSearch = (searchWord: string) => haystack?.some((name) => toSafeString(name).toLowerCase().includes(searchWord))

  const lcSearchTerm = searchTerm?.toLowerCase() || ''

  switch (mode) {
    case 'split-words': {
      return lcSearchTerm.split(' ').every((searchWord) => nonRegexSearch(searchWord))
    }

    case 'regex': {
      try {
        const regex = new RegExp(searchTerm, 'i')
        return haystack?.some((name) => regex.test(toSafeString(name).toLowerCase()))
      } catch (err) {
        return nonRegexSearch(searchTerm)
      }
    }

    default: {
      return nonRegexSearch(lcSearchTerm)
    }
  }
}

export const search = (haystack: string, search: string) => haystack.toLowerCase().includes(search.toLowerCase())

export const getLocaleString = (culture: Culture) => (culture ? `${culture.language?.substring(0, 2).toLowerCase()}-${culture.country?.toUpperCase()}` : '')

export const getPublicAsset = (filePath: string) => `/images/${filePath}`

export const arrayToRecord = <T, V extends string = string>(
  arr: Array<T | undefined>,
  indexKey: keyof T,
  keyFormattingCb: (_item: unknown) => string = toSafeString,
) =>
  arr?.reduce((acc: Partial<Record<V, T>>, item) => {
    if (item) {
      const key = keyFormattingCb(item[indexKey])
      acc[key as V] = item
    }
    return acc
  }, {})

export const arrayToMap = <T, V extends string | number = string>(
  arr: Array<T | undefined>,
  indexKey: keyof T,
  keyFormattingCb: (_item: unknown) => string = toSafeString,
) =>
  arr?.reduce((acc, item) => {
    if (item) {
      const key = keyFormattingCb(item[indexKey])
      acc.set(key as V, item)
    }
    return acc
  }, new Map<V, T>())

export const arrayToSet = <T, V extends string | number = string>(
  arr: Array<T | undefined>,
  indexKey: keyof T,
  keyFormattingCb: (_item: unknown) => string = toSafeString,
) =>
  arr?.reduce((acc, item) => {
    if (item) {
      const key = keyFormattingCb(item[indexKey])
      acc.add(key as V)
    }
    return acc
  }, new Set<V>())

export const compareLexioGraphically = (a: string, b: string) => {
  return toComparable(a).localeCompare(toComparable(b))
}

export const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth

export const getVerticalScrollbarHeight = () => window.innerHeight - document.documentElement.clientHeight

export const getActiveFilters = (query: Record<string, string | (string | null)[] | null>) => Object.entries(query).filter(([key]) => key?.startsWith('_'))

export const cultureIsIncluded = (selectedCultures: Culture[] | undefined, culture: Culture) =>
  selectedCultures?.find((selectedCulture) => selectedCulture.country === culture.country && selectedCulture.language === culture.language)

export const addToArray = (arr: any[], obj: any, index?: number) => {
  if (index !== undefined) {
    arr.splice(index, 0, obj)
  } else {
    arr.push(obj)
  }
}

export const moveArrayItem = (arr: any[], index: number, deltaChange: number) => {
  const newIndex = index + deltaChange
  if (newIndex >= 0 && newIndex < arr.length) {
    const temp = arr[newIndex]
    arr[newIndex] = arr[index]
    arr[index] = temp
  }
}

export const deleteArrayItem = (arr: any[], index: number, confirmMessage?: string) => {
  if (!confirmMessage || confirm(confirmMessage)) {
    arr.splice(index, 1)
  }
}

/**
 * Filters empty values and joins
 */
export const joinArray = (arr: Array<string | number | undefined>, joiner = ', ') => arr.filter((value) => value || value === 0).join(joiner)

// let debounceVar: number | undefined
// const debounce = (cb: Callback) => {
//   clearTimeout(debounceVar)
//   debounceVar = window.setTimeout(() => cb())
// }
export const getYoutubeId = (fullLink: string | undefined) => {
  if (fullLink) {
    try {
      const idFromQuery = new URL(fullLink).searchParams.get('v')
      const idFromParam = fullLink
        ?.split('/')
        .filter((part) => !!part)
        .slice(-1)
        .join('')
      return idFromQuery || idFromParam
    } catch (err) {
      console.log(err)
    }
  }
}

export const getYoutubeLink = (id: string | undefined, includeQuerySettings = true) => {
  if (id) {
    const query = '?controls=1;mute=1;showinfo=0;autoplay=0'
    const embedLink = `https://www.youtube.com/embed/${id}`
    return embedLink + (includeQuerySettings ? query : '')
  }
}

export const getYoutubeThumbnail = (id: string | undefined) => `https://img.youtube.com/vi/${id}/mqdefault.jpg` || ''

export const slotHasContent = (slot: Slot | undefined) =>
  !!slot?.()?.reduce((value, node) => value || (node.type.toString() !== 'Symbol(v-cmt)' && node.type !== Comment && node.type !== null), false)

export const slotHasContent2 = (slot: Slot | undefined) => slot?.()?.some((node) => node.type !== Comment && (node.children || (node as any).text?.trim()))

export const waitForTimeout = (waitMs?: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, waitMs)
  })

export const getCountrySymbols = (countries?: Country[]) => countries?.map((country) => CountryFlagSymbols[country]).join(' ') || ''

export const timeHasPassed = (timestampMs: number, intervalMs: number) => Date.now() > timestampMs + intervalMs

const cachedColourStrings: { [colour: string]: boolean } = {}
export const isValidCssColour = (str: string) => {
  if (cachedColourStrings[str] === undefined) {
    const element = document.createElement('div')
    element.style.color = str
    cachedColourStrings[str] = !!element.style.color
  }
  return cachedColourStrings[str]
}

export const decodeHtmlEntities = (str: string) => {
  const txt = document.createElement('textarea')
  txt.innerHTML = str
  return txt.value
}

export const textIsSelected = () => {
  const selection = window.getSelection()
  return selection !== null && selection.toString().length > 0
}

export const clearTextSelection = (): void => {
  const selection = window.getSelection()
  selection?.removeAllRanges()
}

export const sanitizeRegex = (pattern: string): string => pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const getHierarchicalItems = <T extends SimplifiedCategory>(items: T[] | undefined, parentId?: string) =>
  items?.filter((item) => item.parentId === parentId)

const isMacUser = navigator.userAgent.toUpperCase().startsWith('MAC')
export const metaKey = isMacUser ? 'Cmd' : 'Ctrl'

export const getCountryFlag = (country?: string | 'world', style = 'shiny' as 'flat' | 'shiny') => {
  let flag: Country | string | undefined = country

  if (flag?.toLowerCase() === 'uk') {
    flag = 'gb'
  }

  return `/images/flags/flagsapi/shiny/${flag?.toLowerCase()}.png`
  // return `https://flagsapi.com/${flag?.toUpperCase()}/${style}/64.png`
}

/**
 * Tells whether a string copied from a cell should be quoted or not to prevent cell switching when pasted
 */
export const copiedCellDataShouldBeQuoted = (text: string) => text && /\n|\t/.test(text)

/**
 * Split tabular string data into a two dimensional array
 * Non-quoted rows are split by (carriage return +) newline, and columns by tab
 * @disclaimer Doesn't always split correctly when there are unclosed quotes in cells without line breaks
 */
export const parseTabularData = (str: string) => {
  const rowSplitPattern = /(?<=^[^"]*(?:"[^"]*"[^"]*)*)\r?\n/g

  return str.split(rowSplitPattern).map((row) => {
    return row.split(/\t/).map((str) => {
      const text = copiedCellDataShouldBeQuoted(str) ? str.replace(/^"|"$/g, '').replace(/""/g, '"') : str
      return { text }
    })
  })
}

export const objectIsEmpty = (obj: AnyObject) => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return false
    }
  }
  return true
}

export const findPredicateResult = <T, R>(array: T[] | undefined, predicate: (_item: T) => R | undefined): R | undefined => {
  if (array) {
    for (const item of array) {
      const result = predicate(item)
      if (result !== undefined) {
        return result
      }
    }
  }
}

/**
 * Maps array and only includes predicate that yielded result
 */
export const mapArray = <T, R>(arr: T[] | undefined, callback: (item: T) => R | undefined, initialValue: R[] = []) =>
  arr?.reduce((acc, item) => {
    const result = callback(item)
    result && acc.push(result)
    return acc
  }, initialValue) || []

/** Filters array, accepting only one entry with the unique property (first is always chosen) */
export const filterUniqueEntries = <T>(arr: T[], uniqueKey?: keyof T) => {
  const set = new Set()
  return arr.filter((entry) => {
    const uniqueValue = uniqueKey && typeof entry !== 'string' ? entry[uniqueKey] : entry
    if (!set.has(uniqueValue)) {
      set.add(uniqueValue)
      return true
    }
    return false
  })
}

/**
 * Use when determining which value should be used (priority on editedValue)
 * @returns originalValue if editedValue is undefined, else editedValue
 */
export const getCurrentValue = <T, U>(editedValue: T | null, originalValue: U) => (editedValue !== undefined ? editedValue : originalValue)

export const clearOnEscapeKeyListener = (e: KeyboardEvent, value: string) => {
  if (e.key === 'Escape' && value) {
    e.stopPropagation()
  }
}
/** sanitizes quotation marks and brackets(?) from a string, then splits on line breaks and commas, and finally trims and filters the output */
export const splitSearchTerm = (value: string) =>
  value
    .split(/[\n,]/)
    .map((v) => v.trim().replace(/['[\]]/g, ''))
    .filter(Boolean)

/** Performant join to string of object array */
export const arrayInnerJoin = <T extends object>(array: T[] | undefined, key: keyof T) => {
  if (!array) {
    return
  }

  let result = ''
  for (let i = 0; i < array.length; i++) {
    if (i > 0) {
      result += ', '
    }
    result += array[i][key]
  }

  return result
}

export const getSkuFilterString = (skus: string[]) => `_field_sku=${skus.join(',')}`

export const getProductTypeIconClass = (value: ProductType) => (value === 'product' ? 'fa fa-cube' : value === 'component' ? 'fa fa-puzzle-piece' : '')

export const shouldFormatSku = (sku: string) => /^\d{6,}/.test(sku)
